import { PropsWithChildren } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { Order, RfqOrder } from '../../entities/drfq/orders';
import { useDrfqOrderbook } from '../../contexts/drfqs';
import { useRfqContext } from '../rfq-context';
import { Drfq, isDrfq } from '../../entities/drfq/domain';
import {
  useDrfqOrdersFetcher,
  useTakerOrderbookFetcher,
  useSplittedOrders,
} from './hooks';

export interface OpenAuction {
  readonly state: 'OPEN_AUCTION';
  readonly bids: readonly RfqOrder[];
  readonly asks: readonly RfqOrder[];
}

interface MatchedAuction {
  readonly state: 'MATCHED_AUCTION';
  readonly bids: readonly RfqOrder[];
  readonly asks: readonly RfqOrder[];
  readonly executedBid: Order | null;
  readonly executedAsk: Order | null;
}

// Expired or Canceled. The RFQ should disappear from the table, when it reaches this status.
interface UnmatchedAuction {
  readonly state: 'UNMATCHED_AUCTION';
}

interface LoadingOrders {
  readonly state: 'LOADING_ORDERS';
}

type TakerOrderbookView =
  | LoadingOrders
  | OpenAuction
  | MatchedAuction
  | UnmatchedAuction;

const [useDrfqTakerOrderbook, DrfqOrdersViewCtxProvider] =
  createSafeContext<TakerOrderbookView>('TakerOrderbookView');

export { useDrfqTakerOrderbook };

export default function DrfqTakerOrderbookProvider({
  children,
}: PropsWithChildren) {
  const { rfq: drfq } = useRfqContext();
  const emptyView: TakerOrderbookView = {
    state: 'LOADING_ORDERS',
  };

  if (drfq == null || !isDrfq(drfq)) {
    return (
      <DrfqOrdersViewCtxProvider value={emptyView}>
        {children}
      </DrfqOrdersViewCtxProvider>
    );
  }

  return (
    <FilledDrfqOrdersViewCtxProvider drfq={drfq}>
      {children}
    </FilledDrfqOrdersViewCtxProvider>
  );
}

function FilledDrfqOrdersViewCtxProvider({
  drfq,
  children,
}: PropsWithChildren<{ drfq: Drfq }>) {
  const { isLoading: isLoadingOrderbook } = useTakerOrderbookFetcher(drfq.id);
  const { isLoading: isLoadingOrders } = useDrfqOrdersFetcher(drfq.id, {
    // Fetch only when drfq was executed, to get the executed order
    shouldFetch: drfq.closed_reason === 'EXECUTION_LIMIT',
  });

  const { executedBid, executedAsk } = useSplittedOrders(drfq.id);

  const [bids, asks] = useDrfqOrderbook(drfq.id);

  const view = createView({
    drfq,
    bids,
    asks,
    isLoading: isLoadingOrderbook || isLoadingOrders,
    executedBid,
    executedAsk,
  });
  return (
    <DrfqOrdersViewCtxProvider value={view}>
      {children}
    </DrfqOrdersViewCtxProvider>
  );
}

interface CreateView {
  readonly drfq?: Drfq;
  readonly bids: readonly RfqOrder[];
  readonly asks: readonly RfqOrder[];
  readonly isLoading: boolean;
  readonly executedBid: Order | null;
  readonly executedAsk: Order | null;
}

function createView({
  drfq,
  bids,
  asks,
  isLoading,
  executedBid,
  executedAsk,
}: CreateView): TakerOrderbookView {
  if (isLoading) {
    return { state: 'LOADING_ORDERS' };
  }

  const isClosed =
    drfq != null && ['CLOSED', 'RECENTLY_CLOSED'].includes(drfq.state);

  if (isClosed) {
    if (executedBid !== null || executedAsk !== null) {
      return {
        state: 'MATCHED_AUCTION',
        bids,
        asks,
        executedBid,
        executedAsk,
      };
    }

    return { state: 'UNMATCHED_AUCTION' };
  }

  return {
    state: 'OPEN_AUCTION',
    bids,
    asks,
  };
}
